import React, { useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getActionItems, getClientActionItems, getProviderActionItems, ActionItemsData, ActionItem as ActionItemType } from "api/Dashboard";
import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import ActionItemsModal from "./ActionItemsModal";

const COMPONENT_HEIGHT = 500;

interface ActionItemProps {
  item: ActionItemType;
  onReview: (item: ActionItemType) => void;
}

const ActionItemRow: React.FC<ActionItemProps> = ({ item, onReview }) => {
  const isCritical = item.priority === "critical";
  const isWarning = item.priority === "warning";

  return (
    <Flex
      sx={{
        alignItems: "center",
        gap: "12px",
        padding: "12px 20px",
        position: "relative",
        border: `1px solid ${themevals.colors.inputOutline}`,
        borderRadius: "6px"
      }}
    >
      {/* Priority indicator bar */}
      <Box
        sx={{
          position: "absolute",
          left: "0px",
          top: "0px",
          bottom: "0px",
          width: "4px",
          borderRadius: "2px",
          backgroundColor: isCritical ? themevals.colors.danger : isWarning ? themevals.colors.warning : "transparent",
          borderTopLeftRadius: "6px",
          borderBottomLeftRadius: "6px",
        }}
      />

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            fontWeight: 600,
            color: "base_800",
            lineHeight: 1.4,
            marginBottom: "2px",
          }}
        >
          {item.title}
        </Box>
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            color: "base_500",
            lineHeight: 1.4,
          }}
        >
          {item.subtitle}
        </Box>
      </Box>

      {/* Review button */}
      <BasicButton
        scheme="secondary"
        outline={true}
        onClick={() => onReview(item)}
        styles={{
          flexShrink: "0",
        }}
      >
        Review
      </BasicButton>
    </Flex>
  );
};

interface ActionItemsProps {
  dashboardType?: 'admin' | 'client' | 'provider';
  // onViewAll prop is deprecated - modal is now handled internally
  onViewAll?: () => void;
}

const ActionItems: React.FC<ActionItemsProps> = ({ dashboardType = 'admin', onViewAll }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<ActionItemsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchActionItems = React.useCallback(async () => {
    let result;
    if (dashboardType === 'client') {
      result = await getClientActionItems(5);
    } else if (dashboardType === 'provider') {
      result = await getProviderActionItems(5);
    } else {
      result = await getActionItems(5);
    }
    setData(result);
    setLoading(false);
  }, [dashboardType]);

  // Auto-open modal from email link: ?actionItems=open
  useEffect(() => {
    if (searchParams.get('actionItems') === 'open') {
      setIsModalOpen(true);
      searchParams.delete('actionItems');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    fetchActionItems();
  }, [fetchActionItems]);

  const handleReview = (item: ActionItemType) => {
    // Navigate to the relevant entity
    switch (item.entityType) {
      case "invoice":
        navigate(`/invoices/${item.entityId}`);
        break;
      case "quote":
        navigate(`/quotes/${item.entityId}`);
        break;
      case "project":
        navigate(`/projects/${item.entityId}`);
        break;
      case "purchase_order":
        navigate(`/purchase-orders/${item.entityId}`);
        break;
      default:
        break;
    }
  };

  const handleViewAll = () => {
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "6px",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          boxShadow: "0px 4px 12px 0px rgba(113, 125, 150, 0.05)",
          flex: ["1 1 100%", "0 1 calc(50% - 8px)"],
          minHeight: [`auto`, `${COMPONENT_HEIGHT}px`],
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            padding: "16px 20px",
            borderBottom: "1px solid",
            borderColor: themevals.colors.inputOutline,
            minHeight: "76px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              color: "base_800",
            }}
          >
            Action Items
          </Box>
        </Box>
        <Box sx={{ padding: "20px", flex: 1 }}>
          <Box sx={{ backgroundColor: "#F5F5F5", height: "100%", borderRadius: "4px" }} />
        </Box>
      </Box>
    );
  }

  const items = data?.items || [];
  const totalCount = data?.totalCount || 0;

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        flex: ["1 1 100%", "0 1 calc(50% - 8px)"],
        minHeight: [`auto`, `${COMPONENT_HEIGHT}px`],
        display: "flex",
        flexDirection: "column"
      }}
    >
      {/* Header */}
      <Flex
        sx={{
          padding: "16px 20px",
          borderBottom: "1px solid",
          borderColor: themevals.colors.inputOutline,
          minHeight: "76px",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "14px",
            fontWeight: 600,
            color: "base_800",
          }}
        >
          Action Items {totalCount > 0 && `(${totalCount})`}
        </Box>
        {totalCount > 5 && (
          <BasicButton
            scheme="secondary"
            outline={true}
            onClick={handleViewAll}
          >
            View All
          </BasicButton>
        )}
      </Flex>

      {/* Content */}
      <Box
        sx={{
          padding: "20px 20px",
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          display:"flex",
          flexDirection:"column",
          gap:"20px"
        }}
      >
        {items.length === 0 ? (
          <Flex
            sx={{
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "base_500",
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
            }}
          >
            No action items
          </Flex>
        ) : (
          items.map((item) => (
            <ActionItemRow key={item.id} item={item} onReview={handleReview} />
          ))
        )}
      </Box>

      {/* Modal */}
      {isModalOpen && <ActionItemsModal onClose={() => { setIsModalOpen(false); fetchActionItems(); }} onChange={fetchActionItems} dashboardType={dashboardType} />}
    </Box>
  );
};

export default ActionItems;
